/**
 * Client-side file size validation for DreamForm file uploads.
 *
 * The allowed size per field is rendered onto the <input type="file"> as
 * `data-max-size` (bytes) by site/snippets/dreamform/fields/file-upload.php.
 * We check the selected files via the File API before the form is submitted,
 * so users see a warning immediately instead of after a failed upload.
 */

const FILE_INPUT_SELECTOR = 'input[type="file"][data-max-size]';

export default class FormUploadValidation {

  constructor(selector = '.form') {
    const forms = document.querySelectorAll(selector);
    if (!forms.length) {
      return;
    }

    forms.forEach((form) => this.initForm(form));
  }

  initForm(form) {
    if (form.dataset.uploadValidationInitialized) {
      return;
    }

    const inputs = form.querySelectorAll(FILE_INPUT_SELECTOR);
    if (!inputs.length) {
      return;
    }

    form.dataset.uploadValidationInitialized = 'true';

    inputs.forEach((input) => {
      input.addEventListener('change', () => this.validateInput(input));
    });

    form.addEventListener('submit', (event) => {
      let valid = true;
      inputs.forEach((input) => {
        if (!this.validateInput(input)) {
          valid = false;
        }
      });

      if (!valid) {
        event.preventDefault();
        const firstInvalid = form.querySelector(`${FILE_INPUT_SELECTOR}[aria-invalid="true"]`);
        if (firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstInvalid.focus({ preventScroll: true });
        }
      }
    });
  }

  validateInput(input) {
    const maxSize = parseInt(input.dataset.maxSize, 10) || 0;
    const maxTotalSize = parseInt(input.dataset.maxTotalSize, 10) || 0;
    const maxLabel = input.dataset.maxSizeLabel || formatBytes(maxSize);
    const files = Array.from(input.files || []);

    let message = '';

    if (files.length) {
      const tooBig = maxSize ? files.filter((file) => file.size > maxSize) : [];

      if (tooBig.length === 1) {
        message = `Die Datei „${tooBig[0].name}“ ist ${formatBytes(tooBig[0].size)} groß. `
          + `Erlaubt sind maximal ${maxLabel} pro Datei.`;
      } else if (tooBig.length > 1) {
        message = `${tooBig.length} der ausgewählten Dateien sind größer als ${maxLabel}. `
          + `Bitte wählen Sie kleinere Dateien aus.`;
      }

      if (!message && maxTotalSize) {
        const total = files.reduce((sum, file) => sum + file.size, 0);
        if (total > maxTotalSize) {
          message = `Die ausgewählten Dateien sind zusammen ${formatBytes(total)} groß. `
            + `Insgesamt können maximal ${formatBytes(maxTotalSize)} hochgeladen werden.`;
        }
      }
    }

    this.setError(input, message);
    return !message;
  }

  setError(input, message) {
    const form = input.form;
    const key = (input.name || '').replace(/\[\]$/, '');
    const errorEl = form ? form.querySelector(`[data-error="${cssEscape(key)}"]`) : null;
    const wrapper = input.closest('[data-has-error]');

    if (message) {
      input.setAttribute('aria-invalid', 'true');
      if (errorEl && errorEl.id) {
        input.setAttribute('aria-describedby', errorEl.id);
      }
      if (wrapper) {
        wrapper.setAttribute('data-has-error', 'true');
      }
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.dataset.uploadValidationError = 'true';
      }
    } else {
      input.removeAttribute('aria-invalid');
      if (wrapper) {
        wrapper.setAttribute('data-has-error', 'false');
      }
      // only clear messages we set ourselves, never server-rendered ones
      if (errorEl && errorEl.dataset.uploadValidationError) {
        errorEl.textContent = '';
        delete errorEl.dataset.uploadValidationError;
        input.removeAttribute('aria-describedby');
      }
    }

    this.updateSubmitState(form);
  }

  updateSubmitState(form) {
    if (!form) {
      return;
    }

    const hasError = !!form.querySelector(`${FILE_INPUT_SELECTOR}[aria-invalid="true"]`);
    form.querySelectorAll('button[type="submit"]').forEach((button) => {
      button.disabled = hasError;
      button.setAttribute('aria-disabled', hasError ? 'true' : 'false');
      button.classList.toggle('opacity-50', hasError);
      button.classList.toggle('pointer-events-none', hasError);
    });
  }

}

function formatBytes(bytes) {
  if (!bytes) {
    return '0 MB';
  }

  const mb = bytes / 1024 / 1024;
  if (mb >= 1) {
    return `${mb.toFixed(1).replace('.', ',')} MB`;
  }

  return `${Math.round(bytes / 1024)} KB`;
}

function cssEscape(value) {
  if (window.CSS && typeof window.CSS.escape === 'function') {
    return window.CSS.escape(value);
  }
  return value.replace(/["\\]/g, '\\$&');
}
